/**
 * Agent API for Widget
 *
 * Fetches agent information from Firestore to display in widget header.
 */

import { doc, getDoc } from 'firebase/firestore';
import { getDb } from './firebase';

/**
 * Agent information for widget display
 */
export interface AgentInfo {
  name: string;
  role?: string;
  avatarIndex?: number;
  /** Optional avatar image URL (kept optional for backward compatibility). */
  avatarUrl?: string;
}

/**
 * Fetch allowed origins for an account
 *
 * @param accountId - Account ID
 * @returns Array of allowed origins, or empty array if unrestricted
 */
export async function getAccountAllowedOrigins(accountId: string): Promise<string[]> {
  try {
    const db = getDb();
    const accountRef = doc(db, 'accounts', accountId);
    const snapshot = await getDoc(accountRef);

    if (!snapshot.exists()) {
      console.warn('[LongChat] Account not found:', accountId);
      return [];
    }

    const data = snapshot.data();
    return (data?.settings?.allowedOrigins as string[]) ?? [];
  } catch (error) {
    console.error('[LongChat] Error fetching allowed origins:', error);
    return [];
  }
}

/**
 * Fetch agent information from Firestore
 *
 * @param accountId - Account ID
 * @param agentId - Agent ID
 * @returns Agent info or null if not found
 */
export async function getAgentInfo(
  accountId: string,
  agentId: string
): Promise<AgentInfo | null> {
  try {
    const db = getDb();
    const agentRef = doc(db, 'accounts', accountId, 'agents', agentId);
    const snapshot = await getDoc(agentRef);

    if (!snapshot.exists()) {
      console.warn('[LongChat] Agent not found:', agentId);
      return null;
    }

    const data = snapshot.data();

    return {
      name: data.name || 'צ׳אט תמיכה',
      role: data.role,
      avatarIndex: data.avatarIndex,
      avatarUrl: typeof data.avatarUrl === 'string' ? data.avatarUrl : undefined,
    };
  } catch (error) {
    console.error('[LongChat] Error fetching agent info:', error);
    return null;
  }
}

